import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { getHeroAddresses, type HeroAddressLookup, type Order } from "@/lib/projects";

/** Auftragsnummer für PUT-Payload (wie `resolveEditNummer` in auftraege.js). */
export function resolveEditNummer(project: Order): number {
  const extended = project as Order & {
    nummer?: number | string | null;
    auftragsnummer_num?: number | string | null;
    nummer_num?: number | string | null;
    nummer_int?: number | string | null;
  };

  const direct = extended.nummer;
  if (direct !== undefined && direct !== null && direct !== "") {
    return Number(direct);
  }

  const apiNummer =
    extended.auftragsnummer_num ?? extended.nummer_num ?? extended.nummer_int ?? null;
  if (apiNummer !== undefined && apiNummer !== null && apiNummer !== "") {
    return Number(apiNummer);
  }

  const auftragsnummer = project.auftragsnummer || "";
  const hashMatch = auftragsnummer.match(/#(\d+)/);
  if (hashMatch?.[1]) return Number(hashMatch[1]);

  const trailingDigits = auftragsnummer.match(/(\d+)$/);
  if (trailingDigits?.[1]) return Number(trailingDigits[1]);

  return 0;
}

export function normalizeCustomerId(id: number | null | undefined): number | null {
  if (id == null) return null;
  const parsed = Number(id);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Ansprechpartner und Ort hängen am Kunden.
 * Gleicher Kunde wie beim Öffnen: ursprüngliche Werte.
 * Jeder andere Kunde (oder leer): beide Felder leeren.
 */
/** Stabiler Schlüssel: App-Geschäftspartner vor Hero-ID. */
export function customerSelectionKey(
  heroId: number | null | undefined,
  geschaeftspartnerId: number | null | undefined,
): string | null {
  const gp = normalizeCustomerId(geschaeftspartnerId);
  if (gp != null) return `gp:${gp}`;
  const hero = normalizeCustomerId(heroId);
  if (hero != null) return `hero:${hero}`;
  return null;
}

export function contactAndAddressForCustomer<C, A>(
  nextCustomerId: string | number | null,
  initialCustomerId: string | number | null,
  initialContact: C | null,
  initialAddress: A | null,
): { contact: C | null; address: A | null } {
  if (nextCustomerId != null && nextCustomerId === initialCustomerId) {
    return { contact: initialContact, address: initialAddress };
  }
  return { contact: null, address: null };
}

/** Hero-Adresse für den Bearbeiten-Dialog (Label aus Hero-API, nicht ort_id). */
export async function resolveInitialHeroAddress(
  heroAddressId: number | null | undefined,
  heroCustomerId: number | null | undefined,
  ortName: string | null | undefined,
): Promise<AutocompleteOption<HeroAddressLookup> | null> {
  if (!heroAddressId || heroAddressId <= 0) return null;

  let label = String(ortName || "").trim();
  const labelLooksLikeId = !label || label === String(heroAddressId);

  if (heroCustomerId && labelLooksLikeId) {
    try {
      const list = await getHeroAddresses(heroCustomerId);
      const match = list.find((entry) => entry.address_id === heroAddressId);
      if (match?.title) label = match.title.trim();
    } catch {
      /* optional */
    }
  }

  if (!label || label === String(heroAddressId)) {
    label = `Adresse #${heroAddressId}`;
  }

  return {
    key: String(heroAddressId),
    label,
    value: {
      address_id: heroAddressId,
      title: label,
    },
  };
}
